import api from '../Components/DataManagement/Api';
import config from '../config';

const API_PREFIX = '/ai-agent';

// Apply any existing token/auth headers from your system
const getHeaders = () => {
    const token = localStorage.getItem('userToken'); // Or however Amplo stores it
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getScenarios = async () => {
    return api.get(`${API_PREFIX}/scenarios`, { headers: getHeaders() });
};

export const startSession = async (scenarioId) => {
    const userId = localStorage.getItem('UserId');
    return api.post(`${API_PREFIX}/start`, { scenario_id: scenarioId, user_id: userId }, { headers: getHeaders() });
};

export const getScenarioParameters = async (scenarioId) => {
    return api.get(`${API_PREFIX}/scenario/${scenarioId}/parameters`, { headers: getHeaders() });
};

export const getParameterValues = async (paramId, filter = null) => {
    let url = `${API_PREFIX}/parameter/${paramId}/values`;
    if (filter) {
        url += `?filter=${encodeURIComponent(filter)}`;
    }
    return api.get(url, { headers: getHeaders() });
};

export const getDistinctSubDomains = async (domainId) => {
    return api.get(`${API_PREFIX}/domain/${domainId}/subdomains`, { headers: getHeaders() });
};

export const getSubSubDomains = async (domainId, subdomainName) => {
    return api.get(`${API_PREFIX}/domain/${domainId}/subdomains/${encodeURIComponent(subdomainName)}/subsubdomains`, { headers: getHeaders() });
};

export const getPeerMetrics = async (variableId) => {
    return api.get(`${API_PREFIX}/variable/${variableId}/peers`, { headers: getHeaders() });
};

export const runScenario = async (payload) => {
    const { parameters, session_id, scenario_id, user_id, mode, conversation_history } = payload;
    const p = parameters || {};

    // Mapping internal app parameters to the external API's expected flat structure.
    // We prioritize using the '_label' version of parameters to send string names instead of integer IDs.
    const resolvedVariable = p.Variable_label || p.Variable || p['Sub Category_label'] || p['Sub Category'] || "";
    const resolvedDomain = p.Domain_label || p.Domain || "";
    const resolvedSubDomain = p['Sub Domain_label'] || p['Sub Domain'] || "";

    // The external API requires `variable` to be non-empty in guided mode.
    // If no variable was selected (e.g. Double Materiality flow without variable step),
    // fall back to sub_domain or domain as the variable.
    // If still empty, switch to query mode ONLY if we have a non-empty query;
    // otherwise, fallback to a default variable to keep the guided mode request valid.
    let effectiveVariable = resolvedVariable;
    let effectiveMode = mode || ((p.query || p.custom_query) ? 'query' : 'guided');

    if (effectiveMode === 'guided' && !effectiveVariable) {
        // Try to use sub_domain or domain as fallback
        effectiveVariable = resolvedSubDomain || resolvedDomain;
        if (!effectiveVariable) {
            const trimmedQuery = (p.query || p.custom_query || "").trim();
            if (trimmedQuery) {
                effectiveMode = 'query';
            } else {
                effectiveVariable = "ESG Performance";
            }
        }
    }

    const rawQuery = (p.query || p.custom_query || "").trim();
    const effectiveQuery = effectiveMode === 'query'
        ? (rawQuery || "Provide an overview of ESG performance")
        : rawQuery;

    const externalPayload = {
        mode: effectiveMode,
        query: effectiveQuery,
        company_name: p.company_name || p['Company / Entity_label'] || p['Company / Entity'] || "",
        domain: resolvedDomain,
        sub_domain: resolvedSubDomain,
        variable: effectiveVariable,
        industry: p.Industry_label || p.Industry || "Information Technology",
        year: p.Year_label || p.Year || 2025,
        session_id: session_id ? String(session_id) : null,
        scenario_id: scenario_id || 1,
        user_id: user_id || "74",
        conversation_history: conversation_history || null,
        extra_options: {
            target_score: p['Score Range'] || p['Score Range_label'] || null,
            risk_opportunity: p['Risk / Opportunity_label'] || p['Risk / Opportunity'] || null
        }
    };

    return api.post(config.REACT_APP_AGENTIC_AI_URL + 'agentic/analyze', externalPayload, {
        headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': config.REACT_APP_AGENTIC_AI_KEY
        }
    });
};

export const getSessionMessages = async (sessionId) => {
    return api.get(`${API_PREFIX}/session/${sessionId}`, { headers: getHeaders() });
};

export const getQuickActions = async () => {
    return api.get(`${API_PREFIX}/quick-actions`, { headers: getHeaders() });
};

export const getRecentSessions = async (userId) => {
    return api.get(`${API_PREFIX}/sessions/recent/${userId}`, { headers: getHeaders() });
};

export const recordMessage = async (payload) => {
    return api.post(`${API_PREFIX}/record-message`, payload, { headers: getHeaders() });
};

export const getAllCompanies = async () => {
    const headers = {
        ...getHeaders(),
        "x-tenant-schema": "Amplo111",
        "x-user-id": localStorage.getItem("UserId") || "",
        "x-user-email": sessionStorage.getItem("userEmail") || ""
    };
    return api.get(`/getAllCompanies`, { headers });
};

export const getAllEntities = async (companyId) => {
    const headers = {
        ...getHeaders(),
        "x-tenant-schema": "Amplo111",
        "x-user-id": localStorage.getItem("UserId") || "",
        "x-user-email": sessionStorage.getItem("userEmail") || ""
    };
    return api.get(`/getAllEntities/${companyId}`, { headers });
};


export const getAgenticCompanies = async (search = '') => {
    const url = search
        ? `${config.REACT_APP_AGENTIC_AI_URL}companies?search=${encodeURIComponent(search)}`
        : `${config.REACT_APP_AGENTIC_AI_URL}companies?limit=500`;

    return api.get(url, {
        headers: {
            'X-API-KEY': config.REACT_APP_AGENTIC_AI_KEY
        }
    });
};

export const checkAnalysisStatus = async (uuid) => {
    return api.get(`${config.REACT_APP_AGENTIC_AI_URL}agentic/status/${uuid}`, {
        headers: {
            'X-API-KEY': config.REACT_APP_AGENTIC_AI_KEY
        }
    });
};

export const getAnalysisDetails = async (uuid) => {
    return api.get(`${config.REACT_APP_AGENTIC_AI_URL}agentic/details/${uuid}`, {
        headers: {
            'X-API-KEY': config.REACT_APP_AGENTIC_AI_KEY
        }
    });
};

export default {
    getScenarios,
    startSession,
    getScenarioParameters,
    getParameterValues,
    getDistinctSubDomains,
    getSubSubDomains,
    getPeerMetrics,
    runScenario,
    getSessionMessages,
    getQuickActions,
    getRecentSessions,
    getAllCompanies,
    getAllEntities,
    getAgenticCompanies,
    checkAnalysisStatus,
    getAnalysisDetails,
    recordMessage,
};
